/**
 * `useDiagnosticsStore` — Zustand slice surfacing read-only diagnostic
 * state for the developer-only Diagnostics view (Requirement 13.3).
 *
 * Holds:
 *   - The current `RouterState` snapshot (`routerSnapshot`) so the view
 *     can render active model, cooldowns, and forced override
 *     (Requirement 5.11, 13.3, 13.4).
 *   - The most recent log entries (`recentEntries`, capped at 50) so
 *     the view's fallback timeline never grows unbounded.
 *   - Storage usage estimate (`storageEstimate`) sourced from
 *     `navigator.storage.estimate()` (Requirement 13.3).
 *
 * The slice is read-only from the UI's perspective: the diagnostics
 * view subscribes and renders, but does not write router state. The
 * boot sequence calls `attach(executor)` once after the Model_Router
 * executor is constructed; thereafter `refresh()` pulls a fresh
 * snapshot on demand (e.g. on a "refresh" button press in the
 * diagnostics view, or on a polling interval the view sets up).
 *
 * This slice deliberately does NOT subscribe to executor events; the
 * executor's state is fully observable through `getState()` and a
 * pull-based refresh keeps the slice's surface small. A future
 * iteration can add a push-based subscription if the diagnostics view
 * needs sub-second freshness.
 */

import { create } from 'zustand';

import type { ModelRouterExecutor } from '../domain/modelRouter/executor';
import type { RouterLogEntry, RouterState } from '../domain/modelRouter/types';

/** Number of recent log entries surfaced in the diagnostics view. */
const RECENT_LOG_WINDOW = 50;

/** Lightweight storage-usage projection (Requirement 13.3). */
export interface StorageEstimate {
  /** Bytes used. `0` when the platform does not report a value. */
  usage: number;
  /** Bytes allocated. `0` when the platform does not report a value. */
  quota: number;
}

/** Public slice surface. */
export interface DiagnosticsState {
  /** Snapshot of the executor's `RouterState`, or `null` until attached. */
  routerSnapshot: RouterState | null;
  /** Most recent log entries (chronological). Capped at `RECENT_LOG_WINDOW`. */
  recentEntries: RouterLogEntry[];
  /** Storage estimate, or `null` when not yet refreshed / unsupported. */
  storageEstimate: StorageEstimate | null;
  /** Wire the slice to the running Model_Router executor. */
  attach(executor: ModelRouterExecutor): void;
  /** Pull a fresh snapshot from the executor and `navigator.storage`. */
  refresh(): Promise<void>;
}

export const useDiagnosticsStore = create<DiagnosticsState>((set) => {
  // The executor reference is held in this closure rather than in the
  // slice state because Zustand's reactive equality check treats
  // closure references as stable, and the diagnostics UI only ever
  // reads from snapshots — it never needs to observe the executor
  // identity directly.
  let executorRef: ModelRouterExecutor | null = null;

  return {
    routerSnapshot: null,
    recentEntries: [],
    storageEstimate: null,

    attach(executor: ModelRouterExecutor): void {
      executorRef = executor;
    },

    async refresh(): Promise<void> {
      if (executorRef !== null) {
        const snapshot = executorRef.getState();
        // Slice the trailing window of the log so the diagnostics
        // view never has to render the entire ring buffer.
        const recent = snapshot.log.slice(-RECENT_LOG_WINDOW);
        set({
          routerSnapshot: snapshot,
          recentEntries: recent,
        });
      }

      // Storage estimate is a best-effort read — the API is not
      // available in every browser/environment, and even when it is
      // it can reject (e.g. in private mode).
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.storage !== 'undefined' &&
        typeof navigator.storage.estimate === 'function'
      ) {
        try {
          const est = await navigator.storage.estimate();
          set({
            storageEstimate: {
              usage: est.usage ?? 0,
              quota: est.quota ?? 0,
            },
          });
        } catch {
          // Estimate failure is non-fatal; leave the previous value.
        }
      }
    },
  };
});
